import React, { useEffect, useMemo, useState } from 'react';
import { resources } from '../mockData/mock';
import { ShoppingBag, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import EbookPurchaseModal from '../components/EbookPurchaseModal';
import EbookPurchaseSuccessModal from '../components/EbookPurchaseSuccessModal';
import ebookCover from '../assets/ebook_cover.png';

const Resources = () => {
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  const purchaseState = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      purchase: params.get('purchase'),
      sessionId: params.get('session_id')
    };
  }, []);

  const [successOpen, setSuccessOpen] = useState(purchaseState.purchase === 'success');

  // If returning from Stripe, poll status so backend can finalize transaction and trigger GHL tagging.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const purchase = params.get('purchase');
    if (!sessionId || purchase !== 'success') return;

    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/status/${sessionId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') return;
          if (data.status === 'expired') return;
        }
      } catch (e) {
        // ignore
      }
      if (attempt < 6) setTimeout(() => poll(attempt + 1), 2000);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, []);


  const getIcon = (type) => {
    switch (type) {
      case 'tool':
      case 'ingredient':
        return <ShoppingBag className="h-5 w-5" />;
      default:
        return <ExternalLink className="h-5 w-5" />;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Resources & Recommendations
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            A small collection of Hearthlowe favorites and helpful add-ons.
          </p>
        </div>
      </section>

      {/* Amazon Product Link removed (Resources page will be ebook + one affiliate for now) */}

      {/* Paid Ebook */}
      <section className="py-12 bg-[#f7f5f2] border-b border-[#e8e6e1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-[#e8e6e1] shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2">
                <div className="bg-white p-6 md:p-8">
                  <div className="inline-flex items-center gap-2 bg-[#a8b5a0]/10 px-3 py-1.5 rounded-full mb-4">
                    <Sparkles className="h-4 w-4 text-[#a8b5a0]" />
                    <span className="text-xs font-medium text-[#736c64]">Paid recipe ebook</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1e1919] mb-3">
                    The Hearthlowe Sourdough Kitchen
                  </h2>
                  <p className="text-[#736c64] mb-5">
                    Recipes for active starter and discard baking—created to keep your sourdough routine inspiring and beginner-friendly.
                  </p>

                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-3xl font-semibold text-[#1e1919]">$17</span>
                    <span className="text-sm text-[#736c64] line-through">$27</span>
                    <span className="text-xs text-[#243B29]/80 bg-[#9EAE89]/25 px-2 py-1 rounded-full">Sale</span>
                  </div>

                  <button
                    onClick={() => setPurchaseOpen(true)}
                    className="inline-flex items-center justify-center px-6 py-3 bg-[#a8b5a0] hover:bg-[#8fa087] text-white rounded-lg font-medium transition-colors"
                  >
                    Buy & Get Instant Email Delivery
                  </button>

                  <p className="text-xs text-[#736c64] mt-3">
                    You’ll receive an email from <strong>hello@myhearthlowe.com</strong>. Check spam/promotions if needed.
                  </p>
                </div>

                <div className="bg-[#f7f5f2] p-6 md:p-8 flex items-center justify-center">
                  <img
                    src={ebookCover}
                    alt="The Hearthlowe Sourdough Kitchen ebook cover"
                    className="w-full max-w-sm max-h-[420px] object-contain rounded-xl shadow-lg border border-black/5"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Resources Sections */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {resources.map((section) => (
              <div key={section.id}>
                <h2 className="text-3xl font-bold text-[#1e1919] mb-8">
                  {section.title}
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.items.map((item, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="flex-shrink-0 w-10 h-10 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center text-[#a8b5a0]">
                            {getIcon(item.type)}
                          </div>
                          <div className="flex-grow">
                            <h3 className="font-semibold text-[#1e1919] mb-1">{item.name}</h3>
                          </div>
                        </div>
                        <p className="text-sm text-[#736c64] leading-relaxed mb-4">{item.description}</p>

                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#a8b5a0] hover:bg-[#8fa087] text-white text-sm font-medium transition-colors"
                        >
                          Open on Amazon
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Note About Affiliate Links */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#f7f5f2] rounded-xl p-6 border border-[#e8e6e1]">
            <p className="text-sm text-[#736c64] text-center">
              <strong>Note:</strong> Some links on this page are affiliate links.
              When you purchase through these links, we may earn a small commission at no extra cost to you.
              We only share products we genuinely believe in—and when we collaborate with others, we aim to do it thoughtfully and with integrity.
            </p>
          </div>
        </div>
      </section>
    </div>

      <EbookPurchaseModal open={purchaseOpen} onClose={() => setPurchaseOpen(false)} />
      <EbookPurchaseSuccessModal
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          const url = new URL(window.location.href);
          url.searchParams.delete('purchase');
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url.toString());
        }}
      />
    </>
  );
};

export default Resources;
